import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { changeLanguage } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('minnow_token'));
  const [loading, setLoading] = useState(true);
  const [googleAuthPending, setGoogleAuthPending] = useState(false);
  // Language version counter - increments when language changes to trigger refetch
  const [languageVersion, setLanguageVersion] = useState(0);
  // Skip fetchUser after login/register since we already have user data
  const [skipNextFetch, setSkipNextFetch] = useState(false);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Skip fetch if we just logged in (we already have user data from login response)
    if (skipNextFetch) {
      setSkipNextFetch(false);
      setLoading(false);
      return;
    }
    
    // Check for token OR try to fetch user (for cookie-based auth)
    fetchUser();
  }, [token]);

  // Sync language when user changes
  useEffect(() => {
    if (user?.preferred_language) {
      changeLanguage(user.preferred_language);
    }
  }, [user?.preferred_language]);

  const fetchUser = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { headers });
      const userData = response.data;
      
      // Immediately sync language before setting user state
      // This ensures UI renders in correct language from the start
      if (userData?.preferred_language) {
        changeLanguage(userData.preferred_language);
      }
      
      setUser(userData);
    } catch (error) {
      // Only log out if we had a token and it failed
      if (token) {
        console.error('Auth error:', error);
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Get the language preference from i18n localStorage (set by landing page selector)
    const selectedLanguage = localStorage.getItem('i18nextLng');
    
    const response = await axios.post(`${API}/auth/login`, { 
      email, 
      password,
      preferred_language: selectedLanguage || null
    });
    
    // Store token
    localStorage.setItem('minnow_token', response.data.token);
    
    // Sync i18n language with the user's preferred language
    if (response.data.user?.preferred_language) {
      changeLanguage(response.data.user.preferred_language);
    }
    
    // Set user data directly from login response (skip fetchUser)
    setUser(response.data.user);
    setSkipNextFetch(true);
    setToken(response.data.token);
    setLoading(false);
    
    return response.data;
  };

  const register = async (email, password, name, startTrial = true) => {
    // Get the language preference from landing page (if visitor selected one)
    const landingLanguage = localStorage.getItem('minnow_landing_language');
    
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      start_trial: startTrial,
      preferred_language: landingLanguage || 'en'
    });
    
    // Store token
    localStorage.setItem('minnow_token', response.data.token);
    
    // Sync language if user has a preference
    if (response.data.user?.preferred_language) {
      changeLanguage(response.data.user.preferred_language);
    }
    
    // Set user data directly from register response (skip fetchUser)
    setUser(response.data.user);
    setSkipNextFetch(true);
    setToken(response.data.token);
    setLoading(false);
    
    // Clear the landing language after using it
    localStorage.removeItem('minnow_landing_language');
    
    return response.data;
  };

  // Google OAuth login
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    setGoogleAuthPending(true);
    // Redirect to Emergent Google OAuth
    const currentUrl = window.location.origin;
    const callbackUrl = `${currentUrl}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callbackUrl)}`;
  };

  // Handle Google OAuth callback
  const handleGoogleCallback = async (sessionId) => {
    try {
      setGoogleAuthPending(true);
      
      // Get the language preference from i18n localStorage (set by landing page selector)
      const selectedLanguage = localStorage.getItem('i18nextLng');
      
      const response = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId,
        preferred_language: selectedLanguage || null
      });
      
      if (response.data.success) {
        // Store token for consistency with regular auth
        if (response.data.token) {
          localStorage.setItem('minnow_token', response.data.token);
        }
        
        // Sync language before setting user
        if (response.data.user?.preferred_language) {
          changeLanguage(response.data.user.preferred_language);
        }
        
        // Set user data directly (skip fetchUser)
        setUser(response.data.user);
        setSkipNextFetch(true);
        if (response.data.token) {
          setToken(response.data.token);
        }
        setLoading(false);
        
        return response.data;
      } else {
        throw new Error('Google authentication failed');
      }
    } catch (error) {
      console.error('Google auth error:', error);
      throw error;
    } finally {
      setGoogleAuthPending(false);
    }
  };

  const skipTrial = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.post(`${API}/skip-trial`, {}, { headers });
    await fetchUser();
    return response.data;
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear server-side session
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (error) {
      // Ignore errors during logout
    }
    localStorage.removeItem('minnow_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Update user's preferred language and trigger UI refresh
  const updateLanguage = useCallback(async (newLang) => {
    // Update UI language immediately
    changeLanguage(newLang);
    
    // Update user state locally
    setUser(prev => prev ? { ...prev, preferred_language: newLang } : prev);
    
    // Increment version to trigger refetch in components
    setLanguageVersion(v => v + 1);
    
    // Save to backend (don't await to keep UI snappy)
    if (token) {
      try {
        await axios.put(`${API}/user/settings`, 
          { preferred_language: newLang },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      googleAuthPending,
      languageVersion,
      login, 
      register, 
      loginWithGoogle,
      handleGoogleCallback,
      logout, 
      updateUser,
      updateLanguage,
      fetchUser, 
      skipTrial 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
